import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  Building2,
  BookOpen,
  Award,
  CreditCard,
  AlertCircle,
  ArrowRight,
  TrendingUp,
  Clock,
  CheckCircle,
  FileText,
  Mail,
} from 'lucide-react';

const AdminDashboard = () => {
  const { user, api } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingOrgs, setPendingOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, orgsRes] = await Promise.all([
        api.get('/settings/dashboard-stats'),
        api.get('/organisations?status=pending'),
      ]);
      setStats(statsRes.data);
      setPendingOrgs(orgsRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveOrg = async (orgId, approved) => {
    try {
      await api.post(`/organisations/${orgId}/approve?approved=${approved}`);
      setPendingOrgs(pendingOrgs.filter(org => org.id !== orgId));
      fetchData();
    } catch (error) {
      console.error('Failed to approve organisation:', error);
    }
  };

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      link: '/admin/users',
    },
    {
      title: 'Organisations',
      value: stats?.total_organisations || 0,
      icon: Building2,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      link: '/admin/organisations',
    },
    {
      title: 'Total Courses',
      value: stats?.total_courses || 0,
      icon: BookOpen,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      link: '/admin/courses',
    },
    {
      title: 'Enrollments',
      value: stats?.total_enrollments || 0,
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      link: '/admin/reports',
    },
    {
      title: 'Certificates Issued',
      value: stats?.total_certificates || 0,
      icon: Award,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      link: '/admin/certificates',
    },
    {
      title: 'Payments',
      value: stats?.completed_payments || 0,
      icon: CreditCard,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
      link: '/admin/payments',
    },
    {
      title: 'Enquiries',
      value: stats?.total_enquiries || 0,
      icon: Mail,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-100',
      link: '/admin/enquiries',
    },
  ];

  if (loading) {
    return (
      <DashboardLayout requiredRoles={['super_admin']}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#f8fafc] border-t-[#20acf6]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout requiredRoles={['super_admin']}>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#2a3e5d]">
              Admin Dashboard
            </h1>
            <p className="text-[#1e293b]/60 mt-1">
              Overview of your platform
            </p>
          </div>
          <div className="flex gap-3">
            <Button asChild variant="outline" className="border-[#f8fafc] text-[#1e293b] hover:bg-[#f8fafc]">
              <Link to="/admin/users">Manage Users</Link>
            </Button>
            <Button asChild className="bg-[#20acf6] hover:bg-[#3bb8f7] text-white">
              <Link to="/admin/courses">Manage Courses</Link>
            </Button>
          </div>
        </div>

        {/* Pending Approvals Alert */}
        {stats?.pending_organisations > 0 && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="p-4 flex items-center gap-4">
              <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-yellow-800">
                  {stats.pending_organisations} organisation{stats.pending_organisations > 1 ? 's' : ''} pending approval
                </p>
                <p className="text-sm text-yellow-700">
                  Review and approve new organisation registrations
                </p>
              </div>
              <Button asChild variant="outline" className="border-yellow-600 text-yellow-700 hover:bg-yellow-100">
                <Link to="/admin/organisations?status=pending">Review</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Link key={index} to={stat.link}>
                <Card className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600">{stat.title}</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1">
                          {stat.value.toLocaleString()}
                        </p>
                      </div>
                      <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-6 h-6 ${stat.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Pending Organisations */}
        {pendingOrgs.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">
                Pending Organisation Approvals
              </CardTitle>
              <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                {pendingOrgs.length} pending
              </Badge>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pendingOrgs.slice(0, 5).map((org) => (
                  <div
                    key={org.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-xl"
                  >
                    <div>
                      <h4 className="font-medium text-slate-900">{org.name}</h4>
                      <p className="text-sm text-slate-600">{org.contact_email}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        <Clock className="inline h-3 w-3 mr-1" />
                        Applied {new Date(org.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleApproveOrg(org.id, false)}
                      >
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleApproveOrg(org.id, true)}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-[#f8fafc] hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <BookOpen className="h-8 w-8 text-[#20acf6] mb-4" />
              <h3 className="font-semibold text-[#1e293b] mb-2">Create Course</h3>
              <p className="text-sm text-[#1e293b]/60 mb-4">
                Add new training content to the platform
              </p>
              <Button asChild variant="outline" className="w-full border-[#f8fafc] hover:bg-[#f8fafc]">
                <Link to="/admin/courses/new">
                  Create Course <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="border-[#f8fafc] hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <Users className="h-8 w-8 text-[#02d1d4] mb-4" />
              <h3 className="font-semibold text-[#1e293b] mb-2">Add Content Manager</h3>
              <p className="text-sm text-[#1e293b]/60 mb-4">
                Create a content manager account
              </p>
              <Button asChild variant="outline" className="w-full border-[#f8fafc] hover:bg-[#f8fafc]">
                <Link to="/admin/users?add=1&role=content_manager">
                  Add Manager <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="border-[#f8fafc] hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <FileText className="h-8 w-8 text-[#20acf6] mb-4" />
              <h3 className="font-semibold text-[#1e293b] mb-2">Manage Pages</h3>
              <p className="text-sm text-[#1e293b]/60 mb-4">
                Create and edit custom site pages
              </p>
              <Button asChild variant="outline" className="w-full border-[#f8fafc] hover:bg-[#f8fafc]">
                <Link to="/admin/pages">
                  Pages <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="border-[#f8fafc] hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <Award className="h-8 w-8 text-[#02d1d4] mb-4" />
              <h3 className="font-semibold text-[#1e293b] mb-2">Certificate Templates</h3>
              <p className="text-sm text-[#1e293b]/60 mb-4">
                Customize certificate branding
              </p>
              <Button asChild variant="outline" className="w-full border-[#f8fafc] hover:bg-[#f8fafc]">
                <Link to="/admin/certificates">
                  Manage Templates <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
